import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { mockServices, mockTestimonials } from '../mock';
import SEO from '../components/SEO';
import seoConfig from '../config/seoConfig';

const Home = () => {
  return (
    <>
      <SEO
        title={seoConfig.home.title}
        description={seoConfig.home.description}
        keywords={seoConfig.home.keywords}
        type="website"
      />
      <div className="dark-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-image-wrapper">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c" 
            alt="Modern contemporary office workspace"
            className="hero-background-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="display-huge hero-title">
            IT Solutions and AI Expertise That Drive Real Results
          </h1>
          <p className="body-large hero-subtitle">
            We help organizations close technology gaps, accelerate digital transformation, and scale efficiently with the right IT talent, AI strategy, and custom solutions.
          </p>
          <div className="hero-cta">
            <Link to="/contact" className="btn-primary">
              Start Your Project
              <ArrowRight size={20} />
            </Link>
            <Link to="/services" className="btn-secondary">
              Explore Services
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-section">
        <div className="section-header">
          <h2 className="display-medium">Two Proven Service Lines</h2>
          <p className="body-medium section-subtitle">
            <strong>Technology & Consulting Services</strong> for high-impact IT delivery and <strong>Workforce Solutions</strong> for streamlined talent management—so you get the right experts, the right way.
          </p>
        </div>

        {/* Consulting Services */}
        <div className="service-category">
          <h3 className="heading-2 category-title">Technology & Consulting Services</h3>
          <div className="services-grid">
            {mockServices.slice(0, 11).map((service) => (
              <div key={service.id} className="service-card">
                <h4 className="heading-3">{service.title}</h4>
                <p className="body-medium">{service.shortDesc}</p>
                <Link to={`/services/${service.slug}`} className="service-link">
                  Explore <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Workforce Solutions */}
        <div className="service-category">
          <h3 className="heading-2 category-title">Workforce Solutions</h3>
          <div className="services-grid">
            {mockServices.slice(11).map((service) => (
              <div key={service.id} className="service-card">
                <h4 className="heading-3">{service.title}</h4>
                <p className="body-medium">{service.shortDesc}</p>
                <Link to={`/services/${service.slug}`} className="service-link">
                  Explore <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-section">
        <div className="why-section-content">
          <div className="why-text-content">
            <div className="section-header">
              <h2 className="display-medium">Why genTechStaffing Delivers</h2>
              <p className="body-medium section-subtitle">
                Proven expertise that moves critical IT and AI initiatives forward
              </p>
            </div>
            <div className="why-grid">
              <div className="why-card">
                <CheckCircle2 size={32} className="why-icon" />
                <h3 className="heading-3">72 Hours</h3>
                <p className="body-medium">Average time to deliver qualified candidates for priority roles</p>
              </div>
              <div className="why-card">
                <CheckCircle2 size={32} className="why-icon" />
                <h3 className="heading-3">15+ Years</h3>
                <p className="body-medium">Delivering successful IT and AI projects across North America</p>
              </div>
              <div className="why-card">
                <CheckCircle2 size={32} className="why-icon" />
                <h3 className="heading-3">500+ Projects</h3>
                <p className="body-medium">From RPA automation to enterprise AI implementation</p>
              </div>
              <div className="why-card">
                <CheckCircle2 size={32} className="why-icon" />
                <h3 className="heading-3">100% Compliant</h3>
                <p className="body-medium">Full regulatory compliance with transparent processes and reporting</p>
              </div>
            </div>
          </div>
          <div className="why-image">
            <img 
              src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5" 
              alt="Modern tech workspace with multiple monitors"
              className="section-image"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-header">
          <h2 className="display-medium">Client Success Stories</h2>
        </div>
        <div className="testimonials-with-image">
          <div className="testimonials-grid">
            {mockTestimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <p className="body-medium testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div>
                    <p className="heading-3">{testimonial.client}</p>
                    <p className="body-small">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="testimonials-image">
            <img 
              src="https://images.unsplash.com/photo-1690378820474-b468b8ee64d3" 
              alt="Team collaboration meeting"
              className="section-image"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content-wrapper">
          <div className="cta-image">
            <img 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf" 
              alt="Business professionals collaborating"
              className="section-image"
            />
          </div>
          <div className="cta-text-content">
            <h2 className="display-large">Performance-Driven Results Start Here</h2>
            <p className="body-large cta-subtitle">
              Cut through the complexity and connect with IT talent and AI expertise that delivers measurable outcomes.
            </p>
            <Link to="/contact" className="btn-primary btn-large">
              Let's Connect
              <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;